import React, { useState } from "react";
import { Settings, Menu, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogoutButton, LogoutConfirmDialog } from "@/components/auth/LogoutButtonWithConfirm";
import { useIsMobile } from "@/hooks/use-mobile";
import { useScrollDirection } from "@/hooks/useScrollDirection";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "@/components/theme/ThemeToggle";
import { GymSwitcherMenuItem } from "./GymSwitcherMenuItem";
import { NotificationsPopover } from "@/components/notifications/NotificationsPopover";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import { useTranslation } from "react-i18next";
import { useUserStore } from "@/store/userStore";
import { useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";

interface HeaderProps {
  showSearchBar?: boolean;
  hideMenuButton?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ showSearchBar = false, hideMenuButton = false }) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation("common");
  const { role } = useUserStore();
  const { logoUrl, brandName } = useThemeCustomization();
  const { setOpenMobile } = useSidebar();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { scrollDirection, isNearTop } = useScrollDirection(8, 60);
  const shouldHide = scrollDirection === 'down' && !isNearTop;

  const handleLogoutClick = () => {
    setUserMenuOpen(false);
    setTimeout(() => {
      setLogoutDialogOpen(true);
    }, 200);
  };

  const handleSettingsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUserMenuOpen(false);
    requestAnimationFrame(() => navigate("/admin/settings"));
  };

  const handleProfileClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setUserMenuOpen(false);
    requestAnimationFrame(() => navigate(`/${role === "coach" ? "coach" : "athlete"}/profile`));
  };

  const MenuContent = () => (
    <div className="py-1">
      {role === 'admin' && (
        <button 
          onClick={handleSettingsClick}
          className="flex items-center w-full px-4 py-2 text-sm hover:bg-accent transition-colors cursor-pointer"
        >
          <Settings className="h-4 w-4 mr-2" />
          <span>{t("settings")}</span>
        </button>
      )}

      {role !== 'admin' && (
        <button 
          onClick={handleProfileClick}
          className="flex items-center w-full px-4 py-2 text-sm hover:bg-accent transition-colors cursor-pointer"
        >
          <User className="h-4 w-4 mr-2" />
          <span>{t("profile", "Mon profil")}</span>
        </button>
      )}
      
      <GymSwitcherMenuItem onAfterSwitch={() => setUserMenuOpen(false)} />
      
      <div className="h-px bg-border my-1" />
      
      <div className="px-4 py-2">
        <LogoutButton onClick={handleLogoutClick} />
      </div>
    </div>
  );

  return (
    <>
      {/* Background fixe pour la status bar iOS */}
      <div className="safe-area-bg" />
      
      <header className={cn(
        "fixed-header-mobile lg:auto-hide-header w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
        "transition-transform duration-300 ease-out",
        shouldHide && "-translate-y-full"
      )}>
        <div className="flex items-center justify-between h-14 px-4">
          {!hideMenuButton && (
            <Button 
              variant="ghost" 
              size={isMobile ? "sm" : "icon"} 
              className={cn("flex-shrink-0", isMobile ? "h-8 w-8 p-0" : "")}
              onClick={() => setOpenMobile(true)}
            >
              <Menu className={cn(isMobile ? "h-4 w-4" : "h-5 w-5")} />
              <span className="sr-only">{t("toggleMenu")}</span>
            </Button>
          )}
          
          {role === "coach" && (
            <img src={logoUrl} alt={brandName} className="coach-header-logo h-8 w-auto max-w-28 object-contain" />
          )}

          <div className="flex items-center ml-auto space-x-1">
            <ThemeToggle />
            <LanguageSwitcher />
            <NotificationsPopover isMobile={isMobile} />
            
            {!isMobile ? (
              <Popover open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label={t("userMenu", "Menu")}
                  >
                    <Avatar className="h-8 w-8">
                      <AvatarImage src="https://github.com/shadcn.png" alt="user" />
                      <AvatarFallback>SC</AvatarFallback>
                    </Avatar>
                  </Button>
                </PopoverTrigger>
                <PopoverContent 
                  align="end" 
                  className="w-64 p-0" 
                  sideOffset={8}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="border-b px-4 py-3">
                    <p className="font-semibold text-sm">{t("userMenu", "Menu")}</p>
                  </div>
                  <MenuContent />
                </PopoverContent>
              </Popover>
            ) : (
              <Sheet open={userMenuOpen} onOpenChange={setUserMenuOpen}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative"
                  aria-label={t("userMenu", "Menu")}
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src="https://github.com/shadcn.png" alt="user" />
                    <AvatarFallback>SC</AvatarFallback>
                  </Avatar>
                </Button>
                <SheetContent
                  side="bottom"
                  className="p-0 pb-[calc(env(safe-area-inset-bottom,0px)+0.5rem)]"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="border-b px-4 py-3">
                    <p className="font-semibold text-sm">{t("userMenu", "Menu")}</p>
                  </div>
                  <MenuContent />
                </SheetContent>
              </Sheet>
            )}
          </div>
        </div>
      </header>

      {/* Dialog de confirmation rendu EN DEHORS du Popover/Sheet */}
      <LogoutConfirmDialog 
        open={logoutDialogOpen} 
        onOpenChange={setLogoutDialogOpen} 
      />
    </>
  );
};
